import type { TAgentTokenDeleteStatusUpdateData } from "utils/websockets/types";

import constate from "constate";
import { useLiveUpdatesContext } from "context/liveUpdatesContext";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useAuthorization } from "hooks/useAuthorization";
import { useCompany } from "hooks/useCompany";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import { useLiveUpdate } from "hooks/useLiveUpdate";
import { useLoadingState } from "hooks/useLoadingState";
import { Map } from "immutable";
import { type AgentTokenModel } from "models/AgentTokenModel";
import { useCallback, useEffect, useRef, useState } from "react";
import { gainsightLog } from "utils/gainsight";
import { WebSocketEvents, WebSocketRooms } from "utils/websockets/types";

import {
  createAgentToken as apiCreateAgentToken,
  deleteAgentToken as apiDeleteAgentToken,
  rotateAgentToken as apiRotateAgentToken,
  updateAgentTokenName as apiUpdateAgentToken,
  getAgentTokens,
} from "../api/agentTokens";

export type TAgentTokenRevokeStatus = "failed" | "inProgress";

const useAgentTokenRevokes = (onTokenRevoked: (tokenId: string) => void) => {
  const { user } = useAuthenticatedUser();
  const [revokeStatuses, setRevokeStatuses] = useState(Map<string, TAgentTokenRevokeStatus>());

  const trackRevoke = useCallback((tokenId: string) => {
    setRevokeStatuses((current) => current.set(tokenId, "inProgress"));
  }, []);

  const clearRevoke = useCallback((tokenId: string) => {
    setRevokeStatuses((current) => current.remove(tokenId));
  }, []);

  const clearRevokes = useCallback(() => {
    setRevokeStatuses((current) => current.clear());
  }, []);

  const clearRevokesForMissingTokens = useCallback((tokens: Map<string, AgentTokenModel>) => {
    setRevokeStatuses((current) => current.filter((_, tokenId) => tokens.has(tokenId)));
  }, []);

  const handleAgentTokenDeleteUpdate = useCallback(
    (data: TAgentTokenDeleteStatusUpdateData) => {
      if (data.error) {
        setRevokeStatuses((current) => current.set(data.agentTokenId, "failed"));
        return;
      }

      onTokenRevoked(data.agentTokenId);
      setRevokeStatuses((current) => current.remove(data.agentTokenId));
    },
    [onTokenRevoked],
  );

  useLiveUpdate<TAgentTokenDeleteStatusUpdateData>({
    eventName: WebSocketEvents.AGENT_TOKEN_DELETE_STATUS_UPDATE,
    handleEvent: handleAgentTokenDeleteUpdate,
    roomName: WebSocketRooms.COMPANY,
    shouldJoinRoom: user?.isAdmin ?? false,
  });

  return { clearRevoke, clearRevokes, clearRevokesForMissingTokens, revokeStatuses, trackRevoke };
};

const useAgentTokens = () => {
  const [agentTokens, setAgentTokens] = useState<Map<string, AgentTokenModel> | null>(null);
  const { user } = useAuthenticatedUser();
  const company = useCompany();
  const { enableOpenFgaAuthorization } = useFeatureFlags();
  const { hasPermission } = useAuthorization("company", company?.id);
  const canListAgentTokens = enableOpenFgaAuthorization
    ? hasPermission("can_list_agent_tokens")
    : Boolean(user?.isAdmin);
  const openGlobalErrorModal = useOpenGlobalErrorModal();
  const { isLoading, withLoader } = useLoadingState();

  const removeAgentTokenFromState = useCallback((tokenId: string) => {
    setAgentTokens((current) => current?.remove(tokenId) ?? current);
  }, []);

  const { clearRevoke, clearRevokes, clearRevokesForMissingTokens, revokeStatuses, trackRevoke } =
    useAgentTokenRevokes(removeAgentTokenFromState);

  const loadAgentTokens = useCallback(async () => {
    let tokens: Map<string, AgentTokenModel>;
    try {
      tokens = await withLoader(getAgentTokens());
    } catch (error) {
      openGlobalErrorModal(error as Error);
      tokens = Map();
    }
    setAgentTokens(tokens);
    clearRevokesForMissingTokens(tokens);
    return tokens;
  }, [clearRevokesForMissingTokens, openGlobalErrorModal, withLoader]);

  const getMap = useCallback(async (): Promise<Map<string, AgentTokenModel>> => {
    if (agentTokens) return agentTokens;
    return loadAgentTokens();
  }, [agentTokens, loadAgentTokens]);

  const { socket } = useLiveUpdatesContext();
  const hasDisconnected = useRef(false);
  useEffect(() => {
    const handleDisconnect = () => {
      hasDisconnected.current = true;
    };

    const handleConnect = () => {
      if (!hasDisconnected.current) return;
      hasDisconnected.current = false;
      clearRevokes();
      if (!canListAgentTokens) return;
      void loadAgentTokens();
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [canListAgentTokens, clearRevokes, loadAgentTokens, socket]);

  const createAgentToken = useCallback(
    async (name: string) => {
      try {
        const token = await apiCreateAgentToken(name);
        setAgentTokens((await getMap()).set(token.id, token));
        return token;
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, openGlobalErrorModal],
  );

  const updateAgentToken = useCallback(
    async (id: string, name: string) => {
      try {
        const token = await apiUpdateAgentToken(id, name);
        const updatedToken = (await getMap()).get(token.id)?.set("name", token.name);

        if (!updatedToken) return;
        setAgentTokens((await getMap()).set(token.id, updatedToken));

        return updatedToken;
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, openGlobalErrorModal],
  );

  const rotateAgentToken = useCallback(
    async (tokenId: string) => {
      try {
        const token = await apiRotateAgentToken(tokenId);
        gainsightLog("Agent Token Rotated");
        setAgentTokens((await getMap()).set(token.id, token));
        return token;
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, openGlobalErrorModal],
  );

  const deleteAgentToken = useCallback(
    async (tokenId: string) => {
      trackRevoke(tokenId);
      try {
        await apiDeleteAgentToken(tokenId);
      } catch (error) {
        clearRevoke(tokenId);
        openGlobalErrorModal(error as Error);
      }
    },
    [clearRevoke, openGlobalErrorModal, trackRevoke],
  );

  return {
    actions: { createAgentToken, deleteAgentToken, loadAgentTokens, rotateAgentToken, updateAgentToken },
    state: { agentTokens, isLoading, revokeStatuses },
  };
};

export const [AgentTokensProvider, useAgentTokensContext] = constate(useAgentTokens);
